import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../components/form/CustomButton";
import FiltersPagination from "../../components/elements/FiltersPagination";
import DataTableElement from "../../components/elements/DataTableElement";
import { Columns as SettlementColumns } from "./Columns";
import { Columns as AccountingColumns } from "./Accounting/Columns";
import { settlementsResource } from "../../helpers/configRoutes/backend_helper";
import ConfirmDeleteModal from "../../components/modal/ConfirmDeleteModal";

const SETTLEMENTS_ROUTE = "/settlements";
const ACCOUNTING_SETTLEMENTS_ROUTE = "/accounting-settlements";

const getModeFromPath = (pathname = "", fallbackMode = "manage") => {
    if (pathname.startsWith(ACCOUNTING_SETTLEMENTS_ROUTE)) {
        return "accounting";
    }

    if (pathname.startsWith(SETTLEMENTS_ROUTE)) {
        return "manage";
    }

    return fallbackMode;
};

const Settlement = ({ initialMode = "manage" }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [filters, setFilters] = useState({
        endDate: "",
        startDate: "",
        search: "",
        from: 0,
        to: 0,
        page: 1,
    });
    const [meta, setMeta] = useState({ total: 0, current_page: 0 });
    const [tableLoad, setTableLoad] = useState({
        show: false,
        textLoad: "Loading settlements...",
    });
    const [modalConfirm, setModalConfirm] = useState({
        show: false,
        title: "Delete Settlement",
        loading: false,
        textLoad: "Processing",
    });
    const [data, setData] = useState([]);
    const [mode, setMode] = useState(() => getModeFromPath(location.pathname, initialMode));

    useEffect(() => {
        setMode(getModeFromPath(location.pathname, initialMode));
    }, [initialMode, location.pathname]);

    useEffect(() => {
        const getData = async () => {
            setTableLoad((prev) => ({
                ...prev,
                show: true,
                textLoad: mode === "accounting" ? "Loading accounting settlements..." : "Loading settlements...",
            }));

            const response = await settlementsResource("get", { params: filters });
            setData(response?.settlements || []);
            setMeta(response?.meta || { total: 0, current_page: 0 });

            setTableLoad((prev) => ({ ...prev, show: false }));
        };

        getData();
    }, [filters, mode]);

    const columns = useMemo(() => {
        if (mode === "accounting") {
            return AccountingColumns(navigate, modalConfirm, setModalConfirm, filters);
        }

        return SettlementColumns(navigate, modalConfirm, setModalConfirm);
    }, [filters, modalConfirm, mode, navigate]);

    const deleteSettlement = async () => {
        setModalConfirm((prev) => ({ ...prev, loading: true }));
        const response = await settlementsResource("del", {}, modalConfirm.id);

        if (response?.status) {
            setFilters((prev) => ({ ...prev }));
        }

        setModalConfirm((prev) => ({
            ...prev,
            loading: false,
            show: false,
            id: null,
        }));
    };

    const switchMode = (nextMode) => {
        if (nextMode === mode) {
            return;
        }

        navigate(nextMode === "accounting" ? ACCOUNTING_SETTLEMENTS_ROUTE : SETTLEMENTS_ROUTE);
    };

    const pageCopy = mode === "accounting"
        ? {
            eyebrow: "Settlements center",
            title: "Settlement Accounting",
            description: "Review generated settlements as accounting records, invoices and due dates.",
            helper: "Open a settlement invoice to preview or send it.",
        }
        : {
            eyebrow: "Settlements center",
            title: "Settlements",
            description: "Create, review and manage operator or client settlements from one place.",
            helper: "Create new settlements or open an existing one for details.",
        };

    return (
        <>
            <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
                <div className="max-full mx-auto space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-cs-muted">
                                {pageCopy.eyebrow}
                            </p>
                            <h1 className="text-2xl font-semibold text-cs-dark">
                                {pageCopy.title}
                            </h1>
                            <p className="text-sm text-cs-muted mt-1">
                                {pageCopy.description}
                            </p>
                        </div>

                        <div className="flex flex-col items-start md:items-end gap-2">
                            <div className="inline-flex bg-cs-bg border border-cs-border rounded-2xl p-1 shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => switchMode("manage")}
                                    className={`px-4 py-2 rounded-xl text-sm transition-colors ${mode === "manage"
                                        ? "bg-cs-primary text-white shadow-sm"
                                        : "text-cs-muted hover:text-cs-primary"
                                        }`}
                                >
                                    Settlements
                                </button>
                                <button
                                    type="button"
                                    onClick={() => switchMode("accounting")}
                                    className={`px-4 py-2 rounded-xl text-sm transition-colors ${mode === "accounting"
                                        ? "bg-cs-primary text-white shadow-sm"
                                        : "text-cs-muted hover:text-cs-primary"
                                        }`}
                                >
                                    Accounting
                                </button>
                            </div>

                            <div className="flex flex-col items-start md:items-end text-[9px] text-cs-muted">
                                <span>
                                    Total records:{" "}
                                    <span className="font-semibold text-cs-primary-dark">
                                        {meta.total || 0}
                                    </span>
                                </span>
                                <span className="text-[8px]">{pageCopy.helper}</span>
                            </div>
                        </div>
                    </div>

                    {mode === "manage" && (
                        <div className="flex justify-end">
                            <CustomButton
                                onClick={() => navigate("/addSettlements")}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                            >
                                + Create New Settlement
                            </CustomButton>
                        </div>
                    )}

                    <FiltersPagination
                        showFilters={true}
                        filters={filters}
                        setFilters={setFilters}
                        meta={meta}
                        statusModel={"Settlement"}
                    >
                        <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm overflow-hidden">
                            <DataTableElement columns={columns} items={data} tableLoad={tableLoad} />
                        </div>
                    </FiltersPagination>
                </div>
            </div>

            {mode === "manage" && (
                <ConfirmDeleteModal
                    modal={modalConfirm}
                    setModal={setModalConfirm}
                    action={deleteSettlement}
                />
            )}
        </>
    );
};

export default Settlement;
